import DropdownCommon, {
  DropdownItemStyled,
  DropdownMenuContainer,
  DropdownSearchInput,
} from 'components/common/DropdownCommon';
import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import withAutoScaleToFit from 'components/common/withAutoScaleToFit';
import { DESKTOP_MIN_WIDTH } from 'constants';
import { PAYTO_LOG_TAG } from 'features/to/const';
import { format } from 'helpers';
import { Fade } from 'helpers/animation';
import useInputFocus from 'hooks/useInputFocus';
import useLang from 'hooks/useLang';
import useScrollIntoView from 'hooks/useScrollIntoView';
import { isEmpty, isFunction, isString } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { paragraph500Mixin } from 'styles';
import { arrayUtils, stringUtils } from 'utils';

const CustomMaxAmountDropdown = ({
  field,
  isConfigRequired,
  data,
  keyFind,
  keyTranslate,
  keyProp,
  noResultText,
  placeholder,
  information,
  setInformation,
  validation,
  setValidation,
  onChange,
  onClick,
  isDisabled,
  isSearchHidden,
  amendForcedValidationMessage,
  // DEV-1401 Task 1/2: only ever passed by EditPaytoAccountAgreementModal,
  // for the isAmendBankLimitValidation row. Overrides only the rendered
  // toggle text with the agreement's amendPayToLimit -- does not touch
  // currentSelected, information[field.name], or data/amendMaxAmountOptions,
  // so the six static preset options and the actual submitted value are
  // unaffected. Needed because amendPayToLimit is not guaranteed to be one
  // of the six static presets, so setting it directly as the field's value
  // would silently fall back to data[data.length - 1] in the effect below
  // instead of rendering the real amendPayToLimit text.
  amendForcedDisplayAmount,
  styles = {},
  dropdownStyles = {},
  inputStyles = {},
  toggleStyles = {},
  toggleTextStyles = {},
  dropdownMenuStyles = {},
  dropdownMenuContainerStyles = {},
  dropdownMenuItemStyles = {},
  arrowDownIconColor = '',
  isClearBoth = false,
  scrollUp = false,
  isScalable = false,
  dropdownItemPrefix = '',
  shouldShowLimitGuidance = false,
}) => {
  const { t } = useLang();

  const [searchValue, setSearchValue] = useState('');
  const [dataSearch, setDataSearch] = useState(null);
  const [currentSelected, setCurrentSelected] = useState(null);
  const [isShowDropdown, setShowDropdown] = useState(false);

  const SEARCH_NAME_FIELD = `search-${field.name}`;
  const NO_RESULT_TEXT =
    t(noResultText) ||
    stringUtils.replaceKeyword(t('form_text_no_result_by_label'), [
      { key: 'label', value: t(field.label).toLowerCase() },
    ]);

  const isShowArrowDownIcon = !isDisabled && data?.length > 1;

  const validRef = useRef(null);
  useScrollIntoView(validRef, validation?.[field.name], [validation]);

  useInputFocus(SEARCH_NAME_FIELD);

  useEffect(() => {
    if (isEmpty(data)) return;
    // console.log('information:::', information);
    const item =
      data?.find((c) => {
        // console.log('111x1', isString(c?.[keyFind]));
        // console.log('111x2', isString(information[field.name]?.[keyFind]));
        // console.log('222x1', c?.[keyFind]);
        // console.log('222x2', information[field.name]?.[keyFind]);
        // console.log('333x1', isString(information[field.name]));
        // console.log('444x1', information[field.name]);
        // console.log(
        //   '555x1',
        //   `${dropdownItemPrefix} ${information[field.name]}`
        // );
        return (
          (Boolean(
            isString(c?.[keyFind]) &&
            isString(information[field.name]?.[keyFind]),
          ) &&
            c?.[keyFind] === information[field.name]?.[keyFind]) ||
          (Boolean(
            isString(c?.[keyFind]) && isString(information[field.name]),
          ) &&
            c?.[keyFind] === information[field.name]) ||
          (Boolean(
            isString(c?.[keyFind]) && isString(information[field.name]),
          ) &&
            c?.[keyFind] === `${dropdownItemPrefix} ${information[field.name]}`)
        );
      }) || data[data.length - 1];

    // console.log('item:::', item);
    setCurrentSelected(item);
  }, [information[field.name], data]);

  const formatCurrencyCodeAndMaxAmount = (currencyCodeMaxAmount) => {
    const values = currencyCodeMaxAmount?.split(' ');
    return `${values[0] || ''} ${format.toAmountStr2(values[1])}`;
  };

  const handleSearch = (e) => {
    const { value } = e.target;

    setSearchValue(value);

    if (!value) {
      setDataSearch(null);

      return;
    }

    const newDataSearch = data?.filter(
      (item) =>
        (item?.[keyTranslate]?.toLowerCase()?.includes(value?.toLowerCase()) ||
          item?.[keyFind]?.toLowerCase()?.includes(value?.toLowerCase())) &&
        item,
    );
    setDataSearch(newDataSearch);
  };

  const handleItemSelected = (item) => {
    setCurrentSelected(item);

    setSearchValue('');

    setDataSearch(null);

    setShowDropdown(false);

    const newInformation = {
      ...information,
      [field.name]: keyProp ? item?.[keyProp] : item,
    };
    setInformation(newInformation);

    const newValidation = {
      ...validation,
      [field.name]: '',
    };
    setValidation(newValidation);

    onChange && onChange(item);
  };

  const handleDropdownOnToggle = () => {
    console.log(`⭐ ${PAYTO_LOG_TAG} 👉 handleDropdownOnToggle ➡️ CLICKING ↩`);
    if (isDisabled) {
      setShowDropdown(false);
      return;
    }
    if (isFunction(onClick)) {
      setShowDropdown(false);
      return;
    }

    setSearchValue('');

    setDataSearch(null);

    setShowDropdown(!isShowDropdown);

    if (scrollUp) {
      window.scrollBy({ top: 300, behavior: 'smooth' });
    }
  };

  const handleDropdownOnClick = () => {
    isFunction(onClick) && onClick();
  };

  const renderDropdownToggleChildren = () => {
    const text = amendForcedDisplayAmount
      ? formatCurrencyCodeAndMaxAmount(
          `${dropdownItemPrefix} ${amendForcedDisplayAmount}`,
        )
      : formatCurrencyCodeAndMaxAmount(
          t(currentSelected?.[keyTranslate]) || t(currentSelected?.[keyFind]),
        ) || t(field.placeholder);

    return (
      <>
        {isScalable ? (
          <TextScale id={text} name={field.name}>
            {text}
          </TextScale>
        ) : (
          <Text
            name={field.name}
            style={toggleTextStyles}
            $isPlaceholder={!amendForcedDisplayAmount && isEmpty(currentSelected)}
          >
            {text}
          </Text>
        )}
        <ArrowDownIcon
          color={arrowDownIconColor || 'var(--ds-c-grey-dark)'}
          size={{ width: 14, height: 10 }}
          styles={{
            transform: isShowDropdown && 'rotate(180deg)',
            display: !isShowArrowDownIcon && 'none',
          }}
        />
      </>
    );
  };
  const renderDropdownMenuChildren = () => {
    return (
      <>
        {data?.length > 1 && (
          <>
            {!isSearchHidden && (
              <DropdownSearchInput
                name={SEARCH_NAME_FIELD}
                value={searchValue || ''}
                placeholder={t(
                  placeholder || 'form_placeholder_type_to_search',
                )}
                onChange={handleSearch}
                style={{ ...inputStyles }}
              />
            )}
            <DropdownMenuContainer
              style={{
                borderRadius: isSearchHidden ? '8px' : '0 0 8px 8px',
                transform: isSearchHidden && 'translate(0px, 52px)',
                paddingBottom: 0,
                ...dropdownMenuContainerStyles,
              }}
            >
              {renderDropdownItems(dataSearch || data)}
            </DropdownMenuContainer>
          </>
        )}
      </>
    );
  };
  const renderDropdownItems = (data) => {
    if (arrayUtils.isArrayEmpty(data)) {
      return (
        <DropdownItemStyled
          key={0}
          eventKey={0}
          $isEmpty={arrayUtils.isArrayEmpty(data)}
          style={dropdownMenuItemStyles}
        >
          <NoItemText>{NO_RESULT_TEXT}</NoItemText>
        </DropdownItemStyled>
      );
    }

    return (
      <>
        {data?.map((item, index) => (
          <DropdownItemStyled
            key={index}
            eventKey={index}
            onClick={() => handleItemSelected(item)}
            style={{ textTransform: 'uppercase', ...dropdownMenuItemStyles }}
          >
            {formatCurrencyCodeAndMaxAmount(
              t(item?.[keyTranslate]) || t(item?.[keyFind]),
            )}
          </DropdownItemStyled>
        ))}
      </>
    );
  };

  const getStyleDropdown = () => {
    const common = { ...dropdownStyles };
    return common;
  };
  const getStyleDropdownToggle = () => {
    const common = {
      paddingRight: isShowArrowDownIcon ? '32px' : '10px',
      ...toggleStyles,
    };
    return common;
  };
  const getStyleDropdownMenu = () => {
    const common = {
      boxShadow: isSearchHidden && 'none',
      transform: isSearchHidden && 'translate(0px, 0px)',
      zIndex: isSearchHidden && 1,
      minHeight: '0px',
      ...dropdownMenuStyles,
    };
    return common;
  };
  const getStyleDropdownError = () => {
    const common = {
      position: 'unset',
    };
    return common;
  };

  return (
    <CustomSearchDropdownStyled
      style={{ ...styles }}
      $isClearBoth={isClearBoth}
    >
      <Label ref={validRef}>
        {t(field.label)}
        {(isConfigRequired ?? field.isRequired) && <span>*</span>}
        {shouldShowLimitGuidance && (
          <Desc>
            {t('label_payto_can_increase_limit_per_transaction_desc')}
          </Desc>
        )}
      </Label>
      <DropdownCommon
        placeholder={field?.placeholder}
        keyError={amendForcedValidationMessage ? '' : validation?.[field?.name]}
        isShowDropdown={isShowDropdown}
        onToggle={handleDropdownOnToggle}
        onClick={handleDropdownOnClick}
        isDisabled={isDisabled}
        renderDropdownToggleChildren={renderDropdownToggleChildren}
        renderDropdownMenuChildren={renderDropdownMenuChildren}
        styleDropdown={getStyleDropdown()}
        styleDropdownToggle={getStyleDropdownToggle()}
        styleDropdownMenu={getStyleDropdownMenu()}
        styleDropdownError={getStyleDropdownError()}
      />
    </CustomSearchDropdownStyled>
  );
};

const CustomSearchDropdownStyled = styled.div`
  margin-bottom: 32px;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    ${(props) =>
      props.$isClearBoth &&
      `
        clear: both;
      `}
  }
`;

const NoItemText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  display: flex;
  align-items: center;
  color: #b0b6bd;
  text-transform: initial;

  margin: 0px;
  padding: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--c-primary);

  margin-bottom: 8px;

  & span {
    color: var(--c-required);
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const Desc = styled.p`
  ${paragraph500Mixin};
  color: #1c3f88;
`;

const Text = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  margin: 0;
  padding: 0;
  color: ${(props) =>
    props.$isPlaceholder ? 'var(--ds-c-grey-neutral)' : 'var(--c-primary)'};

  &:hover {
    opacity: 0.7;
  }
`;
const TextScale = withAutoScaleToFit(Text);

export default CustomMaxAmountDropdown;
